import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Chapter, FadeUp } from "./Reveal";
import { WHATSAPP_LINK, INDUSTRY_FEATURES, CREATOR_FEATURES, IMAGES } from "../../data/content";

const Panel = ({ dark, eyebrow, headline, sub, features, image, imageAlt, ctaTestId, children }) => (
    <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className={`relative overflow-hidden rounded-lg flex flex-col ${
            dark ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy"
        }`}
    >
        <div className="p-8 md:p-12 flex-1">
            <p className={`text-xs font-semibold uppercase tracking-[0.3em] mb-6 ${dark ? "text-accent" : "text-accent"}`}>
                {eyebrow}
            </p>
            <h3 className="font-display font-semibold tracking-tight text-3xl md:text-4xl leading-tight">
                {headline}
            </h3>
            <p className={`mt-4 text-base leading-relaxed ${dark ? "text-white/65" : "text-slate-500"}`}>{sub}</p>
            <ul className="mt-8 space-y-3.5">
                {features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-3 text-sm md:text-base ${dark ? "text-white/85" : "text-slate-700"}`}>
                        <Check className={`w-4 h-4 mt-1 shrink-0 ${dark ? "text-accent" : "text-accent"}`} />
                        {f}
                    </li>
                ))}
            </ul>
            {children}
            <motion.a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={ctaTestId}
                whileTap={{ scale: 0.96 }}
                className={`mt-10 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-colors duration-300 ${
                    dark
                        ? "bg-white text-navy hover:bg-accent hover:text-white"
                        : "bg-accent text-white hover:bg-navy"
                }`}
            >
                Chat on WhatsApp
                <ArrowUpRight className="w-4 h-4" />
            </motion.a>
        </div>
        <div className="relative aspect-[16/9] overflow-hidden">
            <img
                src={image}
                alt={imageAlt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
        </div>
    </motion.div>
);

const Split = () => (
    <section id="fit" className="bg-mist py-24 md:py-32" data-testid="split-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
            <Chapter
                index="02"
                label="The Split"
                title={["Where do you fit?"]}
                note="Two tracks. One team. Pick the one built for you."
            />

            <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
                <FadeUp>
                    <Panel
                        dark
                        eyebrow="For Industry"
                        headline="A predictable, automated sales pipeline."
                        sub="For import-export houses and established businesses (₹20cr+). One Sales & Revenue Engine, built, run, and scaled on retainer."
                        features={INDUSTRY_FEATURES}
                        image={IMAGES.industry}
                        imageAlt="ChatProz Sales & Revenue Engine: CRM–ERP, WhatsApp API, chatbots and ads wired into one pipeline"
                        ctaTestId="split-industry-whatsapp-cta"
                    >
                        <p className="mt-8 inline-block rounded-full border border-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                            One engine · On retainer
                        </p>
                    </Panel>
                </FadeUp>
                <FadeUp delay={0.15}>
                    <Panel
                        eyebrow="For Creators"
                        headline="Grow and stay consistent, without living on camera."
                        sub="Your content engine, managed. Your face, optional. Your growth, compounding."
                        features={CREATOR_FEATURES}
                        image={IMAGES.creators}
                        imageAlt="ChatProz creator growth system: content engine, AI avatar and growth overview"
                        ctaTestId="split-creators-whatsapp-cta"
                    >
                        <p className="mt-8 inline-block rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                            Products + done-for-you growth
                        </p>
                    </Panel>
                </FadeUp>
            </div>
        </div>
    </section>
);

export default Split;
